// data normalization methods
// Record obj -> 'field', 'value' map
import { SearchResult } from "../appComponents/Blocks/Composites/SearchResult/SearchResult";
import * as StateLib from "./StateLib";
import { GetDoc, GetWSMgr } from "./StateLib";
import { isNullish } from "@theorgchartdev/storybook";
import { SanitizeFieldValue } from "@/appComponents/Blocks/Cards/RecordInfoCard/utils";
import { DeleteLayoutField } from "@/appComponents/Navigation/MainToolbar/BoxLayoutEditorEditMenu/BoxLayoutEditorEditMenu";
import { FormatFieldValue, sanitizeBlockedValue } from "@coreapp/utils/FormatFieldValue";
import { GetPicSource } from "@coreapp/utils/GetPicSource";
import { DecimalPlaces } from "@coreapp/utils/DecimalPlaces";
import {
  kDPIScreen,
  enumFieldType,
  enumLocaleOptions,
  enumChartTemplateSet,
  enumObjClassNames,
  kEmployeeAssignmentStatusID,
  AssignmentStatus,
  enumObjectTypes,
  kPositionTitleID,
  kNameID,
  kEmployeePositionID,
  kParentPositionID,
  kSupervisorID,
  kAuxSuperID,
  kObjtype
} from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";
import { BoxChip } from "./BoxChip";
import { QObjDBFieldDef } from "@coreapp/CoreApp/QObjDBFieldDef";
import { filterInternalFieldDefs } from "@/Utils/FilterInternalFields";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { TABLE_INITIAL_STATE } from "@coreapp/CoreApp/position-management.constants";

export function normalizeRecordData(
  recordData,
  doFormat = true,
  className = enumObjClassNames.box
) {
  return Object.entries(recordData).map(([field, value]) =>
    getFieldValuePair(field, value, doFormat, className)
  );
}

export function getFieldValuePair(
  fieldName,
  rawValue,
  doFormat = true,
  className = enumObjClassNames.box
) {
  const fieldDef = GetFieldDefFromName(fieldName, className);
  const fieldLabel = fieldDef?.GetFieldLabel();
  const fieldObjectName = fieldDef?.GetFieldName() ?? fieldName;
  const formatName = fieldLabel && fieldLabel.length > 0 ? fieldLabel : fieldName;
  const formatValue = fieldDef && doFormat ? SanitizeFieldValue(rawValue, fieldDef) : rawValue;
  return {
    field: formatName,
    value: formatValue,
    fieldName: fieldObjectName,
    fieldDef: fieldDef
  };
}

// returns new obj of filtered record data based on set of fields you want to keep
// optional normalize input for returning normalized structure for component ingestion
export function getRecordDataByFieldExclusion(
  recordData,
  removeFields,
  normalize,
  formatFieldValue = true,
  className = enumObjClassNames.box
) {
  let trimmedRecordData = { ...recordData };
  for (const key in recordData) {
    if (removeFields.includes(key)) delete trimmedRecordData[key];
  }
  if (normalize) return normalizeRecordData(trimmedRecordData, formatFieldValue, className);
  return trimmedRecordData;
}

/**
 *
 * @param {*} recordData
 * @param {*} includeFields
 * @param {*} normalize
 * @param {boolean=} formatValue
 * @param {string=} className
 * @param {boolean=} respectSorting
 * @returns {*}
 */
export function getRecordDataByFieldInclusion(
  recordData,
  includeFields,
  normalize,
  formatValue = true,
  className = enumObjClassNames.box,
  respectSorting = false
) {
  let trimmedRecordData = {};
  if (respectSorting) {
    for (const key of includeFields) {
      if (key in recordData) trimmedRecordData[key] = recordData[key];
    }
  } else {
    for (const key in recordData) {
      if (includeFields.includes(key)) trimmedRecordData[key] = recordData[key];
    }
  }
  if (normalize) return normalizeRecordData(trimmedRecordData, formatValue, className);
  return trimmedRecordData;
}

export const GetFieldDefsFromList = (fieldNames, useFieldDefs = false) => {
  let doc = StateLib.GetDoc();
  if (!doc || !fieldNames) return [];
  let fieldDefList = [];
  for (let fieldName of fieldNames) {
    const fieldDef =
      useFieldDefs && typeof fieldName === "object" && fieldName
        ? fieldName
        : GetFieldDefFromName(fieldName);
    if (fieldDef && fieldDefList.indexOf(fieldDef) == -1) fieldDefList.push(fieldDef);
  }
  return fieldDefList;
};

/**
 *
 * @param {string} fieldName
 * @param {string=} objClassName
 * @param {boolean=} doKeyWordLookUp
 * @param {QXDoc | null} customDoc
 * @returns {(QObjDBFieldDef | null)}
 */
export const GetFieldDefFromName = (
  fieldName,
  objClassName = enumObjClassNames.box,
  doKeyWordLookUp = false,
  customDoc = null
) => {
  let doc = customDoc ?? StateLib.GetDoc();
  if (!doc || !fieldName) return null;
  let objDB = doc.GetObjDB();
  let objClass = objDB.GetClassByName(objClassName);
  if (!objClass) return null;
  let fD = objClass.GetFieldDefByID(fieldName);
  if (!fD) fD = objClass.GetFieldDefByName(fieldName);
  if (!fD && doKeyWordLookUp) fD = objClass.GetFieldDefByKeyWord(fieldName);
  return fD;
};

export const FormatValueFromFieldName = (fieldName, value) => {
  let unformattedValue = value;
  let formatValue = unformattedValue;
  let fieldDef = GetFieldDefFromName(fieldName);
  if (fieldDef) formatValue = FormatFieldValue(unformattedValue, fieldDef);
  return formatValue;
};

export const GetBoxPhoto = (f, dataObj, dataValue, callback, ignoreFieldDef = false) => {
  var theImage = null;
  if (f?.GetFieldType() == enumFieldType.image || ignoreFieldDef) {
    //TODO - it would be nice for MRB's to possibly show something in the profile panel.
    //if (cB.GetContainerNode() != null && cB.GetContainerType() > enumContainerType.nonmrb)
    //    return null;

    let picDesc = GetPicSource(dataObj, f, dataValue);
    let pDir = picDesc.fDirectory;
    let pFile = picDesc.fFileName;

    let doc = StateLib.GetDoc();
    theImage = doc.FindPicture(pDir, pFile);

    if (theImage == null) {
      var picLoading = doc.PictureLoading(pDir, pFile, callback);
      if (!picLoading) doc.LoadPicture(pDir, pFile, callback, null, null, false);
    }
  }
  return theImage;
};

export const GetPhotoFromDataValue = (photoFieldDef, dataValue, callback) => {
  if (!photoFieldDef) return;
  let image = null;
  const picDesc = GetPicSource(null, photoFieldDef, dataValue);
  const pDir = picDesc.fDirectory;
  const pFile = picDesc.fFileName;
  const doc = StateLib.GetDoc();
  image = doc.FindPicture(pDir, pFile);

  if (image == null) {
    // PictureLoading covers queued pictures as well as downloading ones.
    const picLoading = doc.PictureLoading(pDir, pFile, callback);
    if (!picLoading) doc.LoadPicture(pDir, pFile, callback, null, null, false);
  }

  return image;
};

export const CapitalizeFirstWord = str => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const BuildFieldOptions = (onFieldSelect, value) => {
  let wsMgr = StateLib.GetWSMgr();
  let fields = wsMgr.GetDoc().GetFieldList2(true, false, false, false);
  let options = [];
  for (let field of fields) {
    let option = {};
    option.clickHandler = onFieldSelect;
    option.label = field.GetFieldLabel();
    option.value = field.GetFieldName();
    option.eventInfo = value;
    options.push(option);
  }
  return options;
};

export const unfilterableFields = [
  "_Span Of Control",
  "_Subordinates",
  "_Headcount",
  "_Level",
  "_SubChart Level",
  "_Peers",
  "Persist",
  "Locked",
  "_objInfo",
  "treeID",
  "scFlag",
  "Top Of SubChart",
  "SuperIDLookup"
];

const unsortableFields = [
  ...unfilterableFields,
  "ID",
  "Level",
  "Orphan",
  "Peers",
  "ReportsToName",
  "SubChart Level"
];

export const enumSystemFieldsMode = {
  all: 0,
  filterable: 1,
  sortable: 2
};

export const GetFieldBlockList = (mode, additionalBlocks = []) => {
  const modeBlockLists = {
    [enumSystemFieldsMode.filterable]: unfilterableFields,
    [enumSystemFieldsMode.sortable]: unsortableFields
  };

  return [...(modeBlockLists[mode] || []), ...additionalBlocks];
};

export const GetInitialFields = (isPositionChart, dataTestIdPrefix) => {
  if (!isPositionChart) {
    return { options: [] };
  }

  const employeeFields = [];
  const positionFields = [];
  const options = [
    {
      label: appstr("Employee fields"),
      value: "employeeFields",
      dataTestId: `${dataTestIdPrefix}-employeeFields`,
      keepOpenOnClick: true,
      options: employeeFields,
      listProps: {
        enableSearch: true,
        inputProps: {
          placeholder: appstr("findfield"),
          dataTestId: `${dataTestIdPrefix}-employeeFields--search`
        }
      }
    },
    {
      label: appstr("Position fields"),
      value: "positionFields",
      dataTestId: `${dataTestIdPrefix}-positionFields`,
      keepOpenOnClick: true,
      options: positionFields,
      listProps: {
        enableSearch: true,
        inputProps: {
          placeholder: appstr("findfield"),
          dataTestId: `${dataTestIdPrefix}-positionFields--search`
        }
      }
    }
  ];

  return { options, employeeFields, positionFields };
};

export const BuildSystemFieldOptions = (
  onFieldSelect,
  eventInfo = {},
  mode = enumSystemFieldsMode.filterable,
  dataTestIdPrefix = null,
  blockListParam = [],
  objClassNames = null
) => {
  const doc = StateLib.GetDoc();
  if (!doc) {
    return [];
  }

  const blockList = GetFieldBlockList(mode, blockListParam);

  const isPositionChart = doc.IsPositionDocument();
  let classNames = objClassNames ?? ["Box"];
  if (isPositionChart && objClassNames == null) {
    classNames.push("Position");
  }

  const objDb = doc.GetObjDB();
  const fields = classNames
    .flatMap(className => {
      const objClass = objDb.GetClassByName(className);
      return doc.GetFieldList2(true, true, false, false, false, true, objClass);
    })
    .filter(field => {
      // See QDocumentConfigPanel.prototype.CreateFieldList
      return !blockList.includes(field.GetFieldName());
    });

  const filteredFields = filterInternalFieldDefs(fields);

  const { options, employeeFields, positionFields } = GetInitialFields(
    isPositionChart && classNames.length > 1,
    dataTestIdPrefix
  );

  const addOption = (option, fieldDef) => {
    if (classNames.length === 1) {
      options.push(option);
      return;
    }

    if (fieldDef.GetFieldClassName() === enumObjClassNames.position) {
      positionFields.push(option);
    } else {
      employeeFields.push(option);
    }
  };

  const uniqueValues = new Set();
  for (const fieldDef of filteredFields) {
    let option = {};
    const value = `${fieldDef.GetFieldClassName()}-${fieldDef.GetFieldName()}`;
    if (uniqueValues.has(value)) continue;

    option.clickHandler = onFieldSelect;
    option.label = fieldDef.GetFieldName();
    option.value = value;

    option.eventInfo = {
      ...eventInfo,
      fieldDef
    };

    if (dataTestIdPrefix) {
      option.dataTestId = `${dataTestIdPrefix}-${fieldDef.GetFieldName()}`;
    }

    addOption(option, fieldDef);
    uniqueValues.add(value);
  }

  if (options[0]?.options) {
    for (const subList of options) {
      subList.options.sort((a, b) => a.label.localeCompare(b.label));
    }
  } else {
    options.sort((a, b) => a.label.localeCompare(b.label));
  }

  return options;
};

/*
  Creates a list of field options (for selects) from a list of field names (they don't need to be defined in the
  object class).

  If you have a position chart, the fields param should be an object with two arrays: { enumObjectTypes.holder: [], enumObjectTypes.position: [] }
  If you have an employee chart, the fields param should be a simple array of field names: []
*/
export const BuildTextFieldsOptions = (
  fields,
  onFieldSelect,
  eventInfo = {},
  mode = enumSystemFieldsMode.filterable,
  dataTestIdPrefix = null,
  blockListParam = []
) => {
  const blockList = GetFieldBlockList(mode, blockListParam);
  const isPositionChart = !Array.isArray(fields);

  if (isPositionChart && (!fields[enumObjectTypes.holder] || !fields[enumObjectTypes.position])) {
    return [];
  }

  const { options, employeeFields, positionFields } = GetInitialFields(
    isPositionChart,
    dataTestIdPrefix
  );

  const uniqueValues = new Set();
  const callback = (collection, className) => fieldName => {
    let option = {};
    if (uniqueValues.has(fieldName) || blockList.includes(fieldName)) return;
    option.clickHandler = onFieldSelect;
    option.label = fieldName;
    if (option.label === null) return;
    option.value = `${className}-${fieldName}`;
    option.eventInfo = {
      ...eventInfo
    };
    if (dataTestIdPrefix) {
      option.dataTestId = `${dataTestIdPrefix}-${fieldName}`;
    }
    collection.push(option);
  };

  if (isPositionChart) {
    fields[enumObjectTypes.holder].forEach(callback(employeeFields, enumObjClassNames.box));
    uniqueValues.clear();
    fields[enumObjectTypes.position].forEach(callback(positionFields, enumObjClassNames.position));
  } else {
    fields.forEach(callback(options, enumObjClassNames.box));
  }

  if (options[0]?.options) {
    for (const subList of options) {
      subList.options.sort((a, b) => a.label.localeCompare(b.label));
    }
  } else {
    options.sort((a, b) => a.label.localeCompare(b.label));
  }

  return options;
};

export const BuildChartSystemFieldOptions = (
  chartMetadata,
  onFieldSelect,
  value,
  mode = enumSystemFieldsMode.filterable,
  dataTestIdPrefix = null
) => {
  const blockList = GetFieldBlockList(mode);

  let options = [];
  let fields = chartMetadata["headers"];

  const isPositionChart = chartMetadata.positionChart?.enabled ?? false;
  if (isPositionChart) {
    fields = chartMetadata["positionFields"];
  }

  if (chartMetadata["scriptFormulas"] != null) {
    let dynformulas = chartMetadata["scriptFormulas"].map(function (a) {
      return a.formulaName;
    });
    fields = fields.concat(dynformulas);
  }

  fields = fields.filter(field => {
    // See QDocumentConfigPanel.prototype.CreateFieldList
    return !blockList.includes(field);
  });

  for (let field of fields) {
    let option = {};
    option.clickHandler = onFieldSelect;
    option.label = field;
    option.value = field;
    option.eventInfo = value;
    if (dataTestIdPrefix) {
      option.dataTestId = `${dataTestIdPrefix}-${field}`;
    }
    options.push(option);
  }

  options.sort((a, b) => a.label.localeCompare(b.label));

  return options;
};

export const BuildViewOptions = (onViewSelect, value, excludeTemplateId = null) => {
  let wsMgr = StateLib.GetWSMgr();
  let chartTemplateMgr = wsMgr.GetChartTemplatesMgr();
  var userTemplateList = chartTemplateMgr.GetTemplateList(enumChartTemplateSet.sharedtemplates);
  let options = [];
  for (let view of userTemplateList) {
    if (excludeTemplateId && view.GetTemplateID() === excludeTemplateId) {
      continue;
    }
    let option = {};
    option.clickHandler = onViewSelect;
    option.label = view.GetTemplateName();
    option.value = view.GetTemplateID();
    option.eventInfo = value;
    options.push(option);
  }

  if (options.length === 0) {
    options.push({
      label: appstr("nosearchresults"),
      disabled: true
    });
  }

  return options;
};

export const GetFormattedDateString = (
  dateObj = new Date(),
  includeTime = true,
  simplifyDate = true
) => {
  let wsMgr = StateLib.GetWSMgr();
  let oi = wsMgr.GetOrgInfo();
  let dateFormat = oi.GetDateFormat();
  let timeFormat = oi.GetTimeFormat();
  let localDateStr = dateObj.FormatDate(dateFormat);

  if (!includeTime) return localDateStr;

  let localTimeStr = dateObj.toLocaleString(enumLocaleOptions[Globals._stringMgr.fCurLangStr], {
    hour: "numeric",
    minute: "numeric",
    hour12: (timeFormat ? timeFormat : 12) === 12
  });

  if (!simplifyDate) {
    return localDateStr + " at " + localTimeStr;
  }

  let today = new Date();
  let yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (today.SameDay(dateObj)) localDateStr = appstr("today");
  else if (yesterday.SameDay(dateObj)) localDateStr = appstr("Yesterday");

  return localDateStr + " at " + localTimeStr;
};

export const SortCompareDates = (a, b, reverse = false) => {
  var d1 = new Date(a);
  var d2 = new Date(b);
  return reverse ? d1 - d2 : d2 - d1;
};

const normalizeColor = color => {
  if (typeof color !== "string") {
    return color;
  }

  return color.toUpperCase();
};

export const filterDuplicateColors = (colorList, excludeList = []) => {
  const normalizedExcludeList = excludeList.map(normalizeColor);
  const normalizedList = colorList.reduce((acc, color) => {
    const normalizedColor = normalizeColor(color);
    if (normalizedExcludeList.includes(normalizedColor)) {
      return acc;
    }

    return [...acc, normalizedColor];
  }, []);

  return [...new Set(normalizedList)];
};

export const GetRecentChartResults = doc => {
  const wsMgr = GetWSMgr();
  if (!doc) return [];
  const chartID = doc.GetChartID();
  const user = wsMgr?.GetUserInfo();
  const recentSearches = user?.GetRecentChartSearches(chartID);
  if (!recentSearches || recentSearches.length === 0) return [];
  return recentSearches;
};

/**
 * @param {import("@coreapp/CoreApp/QPlanManager").QXDoc | null | undefined} [doc]
 * @returns {import("@theorgchartdev/storybook").ListItemOption[]}
 */
export const NormalizeRecentOptions = (
  onClick = SelectSearchResult,
  count,
  showNoResultsOption,
  doDBResult,
  handleAsPositionOptions,
  showAssignmentStatus = false,
  doc = null
) => {
  if (!doc) {
    return [];
  }
  const o = doc.GetObjDB();
  const boxClass = o.GetClassByName("Box");
  const positionClass = o.GetClassByName("Position");
  // This is whether or not we are handling positions in the search, not if the open doc is a position chart!
  const isPositionResults = handleAsPositionOptions ?? doc.IsPositionDocument();
  const scm = doc.GetSubChartMgr();
  const personIDDef = scm.GetRecordIDField(boxClass);
  const rawRecentSearchList = GetRecentChartResults(doc);
  const entityIDField =
    isPositionResults && positionClass
      ? scm.GetRecordIDField(positionClass)?.GetFieldName()
      : personIDDef?.GetFieldName();
  let recentSearchList = rawRecentSearchList;

  // Make the properties compliant to universal naming; e.g. charts whose ID is not PersonID.
  // Position charts fields will always be normalized (e.g. use the real field names). Ensure it is kept that way.
  if (!isPositionResults) {
    const nonNormalizedRecentSearchFields = ["PersonID", "Title", "Name"];
    recentSearchList = rawRecentSearchList.map(recentItem => {
      const searchObj = {};
      nonNormalizedRecentSearchFields.forEach(nonNormField => {
        const fieldName = boxClass?.GetFieldDefByID(nonNormField)?.GetFieldName();
        searchObj[fieldName] = recentItem[fieldName] ?? recentItem[nonNormField];
      });
      return searchObj;
    });
  }

  let searchOptions = [];
  let recentSearches = [];

  for (let i = 0; i < recentSearchList.length; i++) {
    let recent = recentSearchList[i];
    let entityID = recent[entityIDField];
    if (recentSearches.includes(entityID)) continue;
    let searchResult = recent;
    if (!doDBResult) {
      searchResult = boxClass.FindObject(personIDDef, entityID, false);
      if (!searchResult) continue;
    }
    let recentOption = {};

    // Avoids the problem with empty recent searches caused by reusage of the same chart ID
    if (!entityID) {
      continue;
    }

    recentOption.clickHandler = onClick;
    recentOption.icon = "clock-rotate-left";
    recentOption.iconProps = { size: "xsmall" };
    recentOption.label = GetResultLabel(searchResult, doc, doDBResult);
    recentOption.isRecent = true;
    const assignmentStatus =
      showAssignmentStatus && !isPositionResults
        ? getAssignmentStatusFromResult(searchResult, doDBResult)
        : null;

    recentOption.content = (
      <SearchResult
        doc={doc}
        result={searchResult}
        textProps={{ modifier: "italic", size: "small" }}
        doDBResult={doDBResult}
        isRecent
        isPositionResult={isPositionResults}
        showAssignmentStatus={showAssignmentStatus}
        assignmentStatus={assignmentStatus}
      />
    );
    recentOption.value = entityID;
    recentOption.result = searchResult;
    searchOptions.push(recentOption);
    recentSearches.push(entityID);
  }

  // display less than entire list
  if (!isNullish(count)) {
    return searchOptions.slice(0, count);
  }
  return searchOptions;
};

const getAssignmentStatusFromResult = (searchResult, doDBResult) => {
  let assignmentStatus;

  if (doDBResult) {
    assignmentStatus = searchResult[kEmployeeAssignmentStatusID];
  } else {
    assignmentStatus = searchResult.SmartGetFieldValue(kEmployeeAssignmentStatusID);
  }

  // Check if the status is valid
  const validStatuses = Object.values(AssignmentStatus);
  if (!validStatuses.includes(assignmentStatus)) {
    return AssignmentStatus.Unassigned;
  }

  return assignmentStatus;
};

/**
 * @param {string | undefined} [recIDFieldName]
 * @param {Function | null} [customResultFormatter]
 * @param {Record<string, unknown> | null} [mapping]
 * @param {import("@coreapp/CoreApp/QPlanManager").QXDoc | null | undefined} [doc]
 * @returns {import("@theorgchartdev/storybook").ListItemOption[]}
 */
export const NormalizeSearchResultOptions = (
  options,
  onClick = SelectSearchResult,
  dataTestIdPrefix = undefined,
  recIDFieldName = undefined,
  doDBResult,
  isPositionChart,
  showAssignmentStatus = false,
  customResultFormatter = null,
  showHoldersAndPositionsSeparately = false,
  mapping = null,
  hideFunctionalLabels = false,
  doc = null
) => {
  if (!doc) return [];
  let searchOptions = [];
  const boxClass = doc.GetObjDB().GetClassByName("Box");
  const recordIDFieldDef = boxClass.GetRecordIDFieldDef();
  const handleAsPositionChart = isPositionChart ?? doc.IsPositionDocument();
  let IDField = recordIDFieldDef?.GetFieldName() ?? "";

  if (recIDFieldName) IDField = recIDFieldName;
  //NOTE: results are dbobj list for static chart docs
  //NOTE: results are flat json list from backend for serverchart searches
  for (let i = 0; i < options.length; i++) {
    let searchResult = options[i];
    let searchOption = {};
    const isFunctionalChartLabel = searchResult["objType"] === enumObjectTypes.functionalLabel;
    if (hideFunctionalLabels && isFunctionalChartLabel) {
      continue; // Should not add the labels to the search results
    }
    const isUnassignedHolder =
      Boolean(searchResult[BoxChip.FREE_HOLDER_LABEL]) ||
      Boolean(searchResult[BoxChip.ASSIGNED_OUT_OF_SCOPE_LABEL]);
    const isOutsidePlan = Boolean(searchResult.isOutsidePlan);
    searchOption.clickHandler =
      isUnassignedHolder || isOutsidePlan ? () => openEmployeeList(doc, searchResult) : onClick;
    const assignmentStatus =
      showAssignmentStatus && !handleAsPositionChart
        ? getAssignmentStatusFromResult(searchResult, doDBResult)
        : null;

    searchOption.content = customResultFormatter ? (
      customResultFormatter(searchResult, doc, doDBResult, showHoldersAndPositionsSeparately)
    ) : (
      <SearchResult
        doc={doc}
        result={searchResult}
        textProps={{ size: "small" }}
        doDBResult={doDBResult}
        isPositionResult={handleAsPositionChart}
        showAssignmentStatus={showAssignmentStatus}
        assignmentStatus={assignmentStatus}
        mapping={mapping}
      />
    );
    searchOption.label = GetResultLabel(searchResult, doc, doDBResult);
    if (dataTestIdPrefix) {
      searchOption.dataTestId = `${dataTestIdPrefix}-${searchOption.label}`;
    }
    if (handleAsPositionChart) {
      const positionClass = doc.GetObjDB().GetClassByName("Position");
      const positionIdField = positionClass?.GetFieldDefByID("PositionID");

      if (doDBResult) {
        if (isUnassignedHolder) {
          searchOption.value = searchResult[recordIDFieldDef?.GetFieldName()];
        } else {
          if (!positionClass || searchResult[kObjtype] === enumObjectTypes.holder) {
            // If searching a PM Chart from an Employee chart. The IDField should've been provided.
            searchOption.value = searchResult[IDField];
          } else {
            searchOption.value = searchResult[positionIdField?.GetFieldName()];
          }
        }
      } else {
        searchOption.value = searchResult.GetObjectRecordID();
      }
    } else {
      searchOption.value = doDBResult ? searchResult[IDField] : searchResult.GetObjectRecordID();
    }
    searchOption.result = searchResult;
    searchOptions.push(searchOption);

    if (
      showHoldersAndPositionsSeparately &&
      searchResult.__HOLDERS__ &&
      Array.isArray(searchResult.__HOLDERS__)
    ) {
      for (let holderResult of searchResult.__HOLDERS__) {
        let holderOption = {};
        holderOption.clickHandler = onClick;

        holderOption.content = customResultFormatter ? (
          customResultFormatter(holderResult, doc, doDBResult, showHoldersAndPositionsSeparately)
        ) : (
          <SearchResult
            doc={doc}
            result={holderResult}
            textProps={{ size: "small" }}
            doDBResult={doDBResult}
            isPositionResult={false}
            showAssignmentStatus={showAssignmentStatus}
            assignmentStatus={null}
          />
        );

        holderOption.label = GetResultLabel(holderResult, doc, doDBResult);
        if (dataTestIdPrefix) {
          holderOption.dataTestId = `${dataTestIdPrefix}-${holderOption.label}`;
        }

        holderOption.value = doDBResult ? holderResult[IDField] : holderResult.GetObjectRecordID();
        holderOption.result = holderResult;
        searchOptions.push(holderOption);
      }
    }
  }
  return searchOptions;
};

const GetResultLabel = (result, doc, doDBResult) => {
  let objType = enumObjectTypes.holder;
  if (doDBResult) {
    if (result["objType"]) {
      objType = result["objType"];
    }
    if (result["__IS_POSITION__"]) {
      objType = enumObjectTypes.position;
    }
    // Functional charts
    if (objType === enumObjectTypes.person) {
      objType = enumObjectTypes.holder;
    }
    const iWizData = doc.GetIWizData();
    const nameField = iWizData.GetNameField(objType);
    return sanitizeBlockedValue(result[nameField]);
  }
  const key = result.IsPositionObj() ? kPositionTitleID : kNameID;
  return sanitizeBlockedValue(result.SmartGetFieldValue(key));
};

export const SelectSearchResult = (target, isHolder = false) => {
  let wsMgr = StateLib.GetWSMgr();
  const canvasPanel = wsMgr.fCanvasPanel;
  canvasPanel.AddToInitialHighlight(target.value);

  let cmdElement = {};
  if (isHolder) {
    const boxClass = wsMgr.GetDoc().GetObjDB().GetClassByName("Box");
    if (!boxClass) return;

    const recordIDFieldDef = boxClass.GetFieldDefByID(kEmployeePositionID);
    const recordIDFieldName = recordIDFieldDef?.GetFieldName();

    cmdElement["targetID"] =
      target?.result?.SmartGetFieldValue?.(kEmployeePositionID) ??
      target.result?.[recordIDFieldName] ??
      target.result?.[kEmployeePositionID];
  } else {
    cmdElement["targetID"] = target.value;
  }

  const isPositionChart = wsMgr.GetDoc().IsPositionDocument() ?? false;

  cmdElement["queryID"] = target.value;
  cmdElement["dbObjRef"] = target.value;
  wsMgr.DoNavToBox(cmdElement, undefined, false, false, !isPositionChart);
  StateLib.ReRenderCanvas();
};

export const openEmployeeList = (doc, searchResult) => {
  const wsMgr = doc.GetWSMgr();
  const nameField = doc.GetIWizData().GetNameField();

  wsMgr.BroadcastTopic("OpenEmployeeList", {
    employeeName: searchResult[nameField]
  });
};

export const GetFieldValueSearchCriteriaText = searchObj => {
  let searchText = "";
  if (!searchObj) return searchText;
  for (const [key, value] of Object.entries(searchObj)) {
    if (!isNullish(value)) {
      const formattedValue = FormatValueFromFieldName(key, value);
      // note: formatted values are purely display. Raw values are used for queries. uncomment below when debugging.
      // console.log(`search value: ${value}`);
      searchText += `${key}: '${formattedValue}';   `;
    }
  }
  return searchText;
};

export const GetUniqueLocalValuesForField = (fieldName, objClassName = enumObjClassNames.box) => {
  const doc = GetDoc();
  if (!doc) return [];
  const fieldDef = GetFieldDefFromName(fieldName, objClassName);
  if (!fieldDef) return [];
  const fieldID = fieldDef.GetFieldID();
  const className = fieldDef.GetFieldClassName();
  const objDB = doc.GetObjDB();
  const boxClass = objDB.GetClassByName(className);
  const objMgr = boxClass.GetObjMgr();
  return objMgr.GetUniqueValuesForField(fieldID).map(v => sanitizeBlockedValue(v));
};

export const GetRecentDocsFromUser = user => {
  var recentDocs = [];
  var tempDocs = [];
  if (!user) return;
  if (user["recentdocs"]) {
    recentDocs = user["recentdocs"];
    recentDocs = recentDocs.filter(function (item) {
      return (
        item["extension"] === "oc5" ||
        item["extension"] === "ocd" ||
        item["extension"] === "ocb" ||
        item["extension"] === "oc5m"
      );
    });
  }
  if (user["tempdocs"]) tempDocs = user["tempdocs"];
  /*
  // TEMPORARILY SUPPRESSED FOR FIRST PASS CHART EXPLORER PHOENIX (UNCOMMENT WHEN READY FOR RECOVERY MODE HANDLING)
  if (tempDocs.length > 0 && false)
  {
      var recoverDocAlert = new QAlert();
      recoverDocAlert.InitConfirmAlert(appstrp("Recover Charts", "?"), appstr("View Unsaved Charts"), this, this.fWSMgr, "DoRecoverDocMode", "", appstr("Review Unsaved Charts"), appstr("Review Later"))
  }
   */
  let docs = {};
  docs.recents = recentDocs;
  docs.unsaved = tempDocs;

  return docs;
};

//================================

export const ConvertPixelsToUnits = (pixels, metric = false) => {
  var msg = "";
  if (metric) {
    var cm = DecimalPlaces((pixels / kDPIScreen) * 2.54, 2);
    msg = cm;
  } else {
    var inches = DecimalPlaces(pixels / kDPIScreen, 2);
    msg = inches;
  }
  return msg;
};

export const ConvertUnitsToPixels = (units, metric = false) => {
  var pixels = 0;
  var dpu = kDPIScreen * (metric ? 2.54 : 1) * units;
  pixels = Math.round(dpu);
  return pixels;
};

export const returnHeadersListNoFormulas = chartConfig => {
  const headers = chartConfig.headers || [];
  const formulaHeaders = chartConfig.formulas?.map(f => f.name) || [];
  return headers.filter(h => !formulaHeaders.includes(h));
};

export const isPositionDocument = () => {
  const doc = GetDoc();
  if (!doc) return;

  return doc.IsPositionDocument();
};

export const getPositionClass = () => {
  const doc = GetDoc();
  const objDB = doc?.GetObjDB();
  return objDB?.GetClassByName(enumObjClassNames.position);
};

export const getBoxClass = () => {
  const doc = GetDoc();
  const objDB = doc?.GetObjDB();
  return objDB?.GetClassByName(enumObjClassNames.box);
};

export const GetAllFieldDefs = (
  includeCalcs = true,
  includeReadOnly = false,
  includePhotos = false,
  includeLocked = false,
  includeInternal = false,
  includeAggregated = true
) => {
  const result = {
    Box: [],
    Position: []
  };

  const doc = GetDoc();

  if (!doc) return result;

  // Get Box fields
  const boxClass = getBoxClass();
  if (boxClass) {
    const boxFields = doc.GetFieldList2(
      includeCalcs,
      includeReadOnly,
      includePhotos,
      includeLocked,
      includeInternal,
      includeAggregated,
      boxClass
    );
    if (boxFields?.length) {
      result.Box = boxFields;
    }
  }

  // Get Position fields
  const positionClass = getPositionClass();
  if (positionClass) {
    const positionFields = doc.GetFieldList2(
      includeCalcs,
      includeReadOnly,
      includePhotos,
      includeLocked,
      includeInternal,
      includeAggregated,
      positionClass
    );
    if (positionFields?.length) {
      result.Position = positionFields;
    }
  }

  return result;
};

export const GetTabularFieldDefs = () => {
  const allFieldDefs = GetAllFieldDefs(true, true);
  const doc = GetDoc();
  const directoryFields = doc?.fDirectoryInfo?.fields;
  if (directoryFields == null) return allFieldDefs;

  const objDb = doc.GetObjDB();
  const directoryFieldNames = new Set(directoryFields);
  const pinnedFieldKeys = new Set(
    Object.entries(TABLE_INITIAL_STATE.columnPinning.left).flatMap(([objClassName, fieldIds]) =>
      fieldIds.map(
        fieldId => `${objClassName}::${objDb.GetMappedFieldDefId(fieldId, objClassName)}`
      )
    )
  );
  const isAllowed = fieldDef =>
    directoryFieldNames.has(fieldDef.GetFieldName()) ||
    pinnedFieldKeys.has(`${fieldDef.GetFieldClassName()}::${fieldDef.GetFieldID()}`);

  return {
    Box: allFieldDefs.Box.filter(isAllowed),
    Position: allFieldDefs.Position.filter(isAllowed)
  };
};

/**
 * Returns localization map for planning field's QObjDBFieldDef field Ids
 * @param {string} objClassName
 * @param {boolean} isPositionPlan
 * @returns {Record<string, string> | undefined}
 */
export const GetPlanFieldsLocalizationMap = (
  objClassName = enumObjClassNames.box,
  isPositionPlan = false
) => {
  const fieldMap = {
    [enumObjClassNames.box]: {
      ...(isPositionPlan
        ? { [kEmployeePositionID]: appstr("Assigned position") }
        : {
            [kSupervisorID]: appstr("Reports to")
          })
    },
    [enumObjClassNames.position]: {
      [kParentPositionID]: appstr("Reports to")
    }
  };

  return fieldMap[objClassName];
};

/**
 * Converts QObjDBFieldDef field Id map to field name map.
 * Can be useful for cases where we have field names instead of ids, for eg. in plan additional fields case
 * @param {Record<string, string>} localizedIdMap
 * @param {string} objClassName
 * @param {QXDoc | null} customDoc
 * @returns {Record<string, string>}
 */
export const GetFieldNameLocalizationMap = (
  localizedIdMap,
  objClassName = enumObjClassNames.box,
  customDoc = null
) => {
  const doc = customDoc ?? GetDoc();
  const objDB = doc?.GetObjDB();
  if (!objDB) return {};
  const nameMap = {};
  Object.entries(localizedIdMap).forEach(([fieldId, localizedStr]) => {
    const fieldDef = objDB.GetClassByName(objClassName)?.GetFieldDefByID?.(fieldId);
    if (fieldDef) {
      const fieldName = fieldDef.GetFieldName();
      if (fieldName) {
        nameMap[fieldName] = localizedStr;
      }
    }
  });
  return nameMap;
};

/**
 * Returns QObjDBFieldDef field names from a list of QObjDBFieldDef field IDs
 * @param {string[]} fieldIds
 * @param {string} objClassName
 * @param {QXDoc | null} customDoc
 * @returns {string[]}
 */
export const GetFieldNamesFromId = (
  fieldIds = [],
  objClassName = enumObjClassNames.box,
  customDoc = null
) => {
  const doc = customDoc ?? GetDoc();
  const objDB = doc?.GetObjDB();
  if (!objDB) return [];

  return fieldIds.map(fieldId => {
    const fieldDef = objDB.GetClassByName(objClassName)?.GetFieldDefByID?.(fieldId);
    return fieldDef ? fieldDef.GetFieldName() : fieldId;
  });
};

/**
 * @param {string} fieldName
 * @param {string} objClassName
 * @param {QXDoc | null} customDoc
 * @returns {string}
 */
export const GetFieldLabelFromName = (
  fieldName,
  objClassName = enumObjClassNames.box,
  customDoc = null
) => {
  const doc = customDoc ?? GetDoc();
  const objDB = doc?.GetObjDB();
  const objClass = objDB?.GetClassByName(objClassName);
  return objClass?.GetFieldLabelFromName(fieldName) || fieldName;
};

/**
 * Gets additional field options for plans excluding specified field Ids and names
 * @param {string[]} excludeFieldIds
 * @param {string[]} excludeFieldNames
 * @param {string} objClassName
 * @param {QXDoc | null} customDoc
 * @returns {string[]}
 */
export const GetPlanAdditionalFieldOptions = (
  excludeFieldIds = [],
  excludeFieldNames = [],
  objClassName = enumObjClassNames.box,
  customDoc = null
) => {
  const doc = customDoc ?? GetDoc();
  const objDB = doc?.GetObjDB();
  if (!objDB) return [];
  const objClass = objDB.GetClassByName(objClassName);
  if (!objClass) return [];
  const readOnlyFields = doc.GetFieldList2(false, true, false, false, false, false, objClass);
  const exclusionList = [...excludeFieldIds];
  readOnlyFields.forEach(field => {
    const fieldID = field.GetFieldID();
    if (fieldID.includes(kAuxSuperID)) {
      exclusionList.push(fieldID);
    }
  });
  const excludedNames = Array.from(
    new Set([...excludeFieldNames, ...GetFieldNamesFromId(exclusionList, objClassName, customDoc)])
  );
  return doc.GetTargetHeaders(false, false, false, false, excludedNames, objClass);
};
